import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles.css';   // 它自己 @import 了 packages/ui/base.css(别再 import 一次:tokens 会重复进 bundle)
import { detectLangFromIP, useT } from './lib/i18n';
import { Auth } from './pages/Auth';
import { Account } from './pages/Account';
import { Connect } from './pages/Connect';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { RequireAuth } from './components/RequireAuth';
import { RequireAdmin } from './components/RequireAdmin';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy-loaded — these pages pull heavy dependencies that don't belong
// on the marketing-page critical path:
//   Admin        — admin-only forms + LLM config UI
//   DslDocs/-Inference — long doc pages, mostly markdown-style content
const WebStudio = lazy(() => import('./pages/WebStudio').then(m => ({ default: m.WebStudio })));
// 登录后的首页 = 桌面端首页(共享 ProjectLibrary + 网页适配器);带着引擎 bundle,懒加载
const WebLibrary = lazy(() => import('./pages/library/WebLibrary').then(m => ({ default: m.WebLibrary })));
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Tiny fallback shown while lazy chunks fetch. ~50ms on a fast connection,
// less than a frame on warm cache, so don't try too hard to be visually rich.
function LazyFallback() {
  const t = useT();
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: 'var(--fg-muted)', fontSize: 13,
    }}>
      <span className="spinner" /> &nbsp;{t('common.loading')}
    </div>
  );
}

// First-visit language detection (IP → zh for Greater China, else en).
// Fire-and-forget: resolves to a no-op for returning visitors; for a fresh
// non-Chinese visitor it flips the layout to English once the request lands.
void detectLangFromIP();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/auth/:page" element={<Auth />} />
            <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
            {/* ★ 2026-07-21:创作整体搬到桌面端,网页只留账号/订阅/分发/MCP。
                /studio · /projects · /library · /templates 四条创作路由退役
                (代码见 tag web-studio-archive-2026-07-21,决策见
                docs/2026-07-21-web-downgrade.md)。老书签统一落到 /download ——
                那一页会解释「创作去哪了」,而不是把人扔回首页自己猜。
                后端对应的 API **没有退役**,远程 MCP 仍在消费它们。 */}
            {/* 2026-10-15:桌面版已放弃,下载下线 —— 老链接落到网页版 */}
            <Route path="/download" element={<Navigate to="/studio" replace />} />
            {/* 自动加字幕 —— 第一个网页确定性工作流(视频→烧录字幕 / 音频→SRT)。
                注册即用 + 免费额度(RequireAuth),和 compile 同一道计费门。 */}
            {/* ★ 2026-10-15 用户:「之前的广告全部下线,网站极其简单」—— 推广页(字幕工作流 / 工作流详情 / 定价 /
                MCP 与 Skill 介绍 / DSL 文档)的路由一律回首页,页面代码留着不删。还在用的功能不动:
                网页版、登录注册、账户、/connect(设备码登录,MCP 与 CLI 要用)、admin;MCP 服务本身在 mcp.autolecture.ai。 */}
            <Route path="/caption" element={<Navigate to="/" replace />} />
            {/* 网页版工作台(Web 迁移 03-D):打开本机工程文件夹 —— 取代 07-21 的「/studio → /download」 */}
            {/* ★ 2026-10-18 用户:「恢复一开始的登录,登录之后的界面设计成和桌面端首页一样」——
                / 和 /studio 都要先登录(同一个 RequireAuth,登录后回到原来要去的地方);/terms /privacy /auth/* 不拦。 */}
            <Route path="/studio" element={<RequireAuth><WebStudio /></RequireAuth>} />
            <Route path="/projects" element={<Navigate to="/studio" replace />} />
            <Route path="/library" element={<Navigate to="/studio" replace />} />
            {/* 工作流市场:网格长在首页(higgsfield 式),这里是单个工作流的详情页。
                /workflows 本身不是独立页 —— 回首页的锚点。 */}
            <Route path="/workflows" element={<Navigate to="/" replace />} />
            <Route path="/workflows/:slug" element={<Navigate to="/" replace />} />
            <Route path="/templates" element={<Navigate to="/studio" replace />} />
            <Route path="/templates/:slug" element={<Navigate to="/studio" replace />} />
            <Route path="/admin" element={
              <RequireAuth>
                <RequireAdmin>
                  <ErrorBoundary label="Admin">
                    <Admin />
                  </ErrorBoundary>
                </RequireAdmin>
              </RequireAuth>
            } />
            <Route path="/docs/dsl" element={<Navigate to="/" replace />} />
            <Route path="/docs/dsl/en" element={<Navigate to="/" replace />} />
            <Route path="/pricing" element={<Navigate to="/" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/account" element={
              <RequireAuth>
                <ErrorBoundary label="Account">
                  <Account />
                </ErrorBoundary>
              </RequireAuth>
            } />
            <Route path="/connect" element={
              <RequireAuth>
                <ErrorBoundary label="Connect">
                  <Connect />
                </ErrorBoundary>
              </RequireAuth>
            } />
            {/* 首页 = 项目库(桌面首页同一份组件)。老的公开首页 pages/Landing.tsx 不再挂路由,代码留着。 */}
            <Route path="/" element={
              <RequireAuth>
                <ErrorBoundary label="Library">
                  <WebLibrary />
                </ErrorBoundary>
              </RequireAuth>
            } />
            <Route path="/mcp" element={<Navigate to="/" replace />} />
            <Route path="/skills" element={<Navigate to="/" replace />} />
            {/* /agent and /cli are legacy paths — the standalone CLI/SDK
                landing was retired (SDK path dropped), so both permanently
                redirect to /mcp, the single developer hub, keeping old
                links alive. */}
            <Route path="/agent" element={<Navigate to="/" replace />} />
            <Route path="/cli" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
